import type {
  AgentActionPayload,
  AgentWidget,
  AgentWidgetAction,
  PresetOption,
} from "@/types/createAgent";

/**
 * Widget → preset conversion, shared by every chat surface that renders or
 * dispatches widget actions. Widgets are the rich v3 UI plan; presets are the
 * compatibility layer the dispatch path (and the terminal harness) consume,
 * so the mapping must stay identical everywhere.
 */
export function presetFromWidgetAction(
  action: AgentWidgetAction
): PresetOption {
  return {
    label: action.label,
    action: action.action,
    actionId: action.actionId,
    presetType: action.presetType,
    data: action.data ?? {},
    actionData: action.actionData,
  };
}

/** A widget's explicit presets win; otherwise its actions map to presets. */
export function getWidgetPresets(widget: AgentWidget): PresetOption[] {
  if (widget.presets && widget.presets.length > 0) {
    return widget.presets;
  }

  return (widget.actions ?? []).map(presetFromWidgetAction);
}

export function getWidgetPresetOptions(
  widgets?: AgentWidget[]
): PresetOption[] {
  return (widgets ?? []).flatMap(getWidgetPresets);
}

function presetMatchesActionSource(
  candidate: PresetOption,
  target: PresetOption
): boolean {
  return (
    candidate.label === target.label &&
    candidate.action === target.action &&
    candidate.actionId === target.actionId
  );
}

/**
 * Resolve the widget a clicked preset came from, so the action payload can
 * carry `{ messageId, widgetId, widgetVersion }` provenance to the server.
 * Message-level compatibility presets still carry the message id even when
 * they did not originate from a widget, so the server can validate them
 * against the latest assistant response.
 */
export function getActionSourceForPreset(
  messageId: string,
  preset: PresetOption,
  widgets: AgentWidget[]
): AgentActionPayload["source"] | undefined {
  for (const widget of widgets) {
    if (
      getWidgetPresets(widget).some(candidate =>
        presetMatchesActionSource(candidate, preset)
      )
    ) {
      return {
        messageId,
        widgetId: widget.id,
        widgetVersion: widget.version,
      };
    }
  }
  return { messageId };
}
